/**
 * The landing page's SEO head + structured data (#103, FR-15.3). One source consumed by
 * both the client hook ({@link useDocumentHead}, applied by {@link ../pages/Landing}) and
 * the build-time prerender ({@link ../prerender-entry}), so the crawled HTML and the
 * hydrated app carry identical metadata. Brand strings come from {@link BRAND_NAME}
 * (FR-9.11) — never hard-coded.
 */

import { BRAND_NAME } from '../ui/brand';
import { canonicalUrl, OG_IMAGE_PATH, PROD_ORIGIN, type DocumentHead } from './head-tags';

/**
 * One-line product pitch reused as the meta description + OG/Twitter description. Kept
 * under ~160 chars so it isn't truncated in search results (#241 SEO audit). Reframed for
 * the Jeopardy-relevance repositioning (#353, FR-15.7, D353.1): leads with the
 * create-a-Jeopardy-style-game intent + the live multiplayer buzzer, using the descriptive
 * "Jeopardy-style" term permitted by FR-9.11.
 */
export const LANDING_DESCRIPTION = `Create custom Jeopardy-style games online with generated questions and a live multiplayer buzzer. Host from your browser and let players join from their phones.`;

/**
 * Standalone description for the `WebApplication` structured-data node (#353, D353.6). The
 * meta {@link LANDING_DESCRIPTION} is CTA-led ("Create custom Jeopardy-style games…"); the
 * structured-data description is a full-sentence product statement that names the brand and
 * the accurate capabilities (generated questions + live multiplayer buzzing) in the
 * Jeopardy-style framing.
 */
export const LANDING_APP_DESCRIPTION = `${BRAND_NAME} is an online customizable Jeopardy-style trivia game maker: generate a full board of questions around any topics, host from your browser, and let players join and buzz in live from their phones.`;

/**
 * JSON-LD `WebApplication`/`VideoGame` node for the landing (FR-15.3, #353 D353.6). Declares
 * the app, its category, and that it's free to try so rich results can surface it as a game.
 */
export function landingJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': ['WebApplication', 'VideoGame'],
    name: BRAND_NAME,
    url: `${PROD_ORIGIN}/`,
    description: LANDING_APP_DESCRIPTION,
    applicationCategory: 'GameApplication',
    operatingSystem: 'Any (web browser)',
    image: `${PROD_ORIGIN}${OG_IMAGE_PATH}`,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
  };
}

/**
 * `Organization` node (#241, D241.7) — pins the brand entity + logo so search engines
 * associate the product name/mark with a single publisher across every page.
 */
export function organizationJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: BRAND_NAME,
    url: `${PROD_ORIGIN}/`,
    logo: `${PROD_ORIGIN}${OG_IMAGE_PATH}`,
  };
}

/**
 * `WebSite` node (#241, D241.7) — declares the canonical site. No `SearchAction`: there
 * is no on-site search to expose a sitelinks search box for.
 */
export function webSiteJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: BRAND_NAME,
    url: `${PROD_ORIGIN}/`,
  };
}

/** The complete head model for `/`. */
export const LANDING_SEO: DocumentHead = {
  title: `${BRAND_NAME} – Custom Online Jeopardy-Style Games`,
  description: LANDING_DESCRIPTION,
  canonicalPath: '/',
  image: OG_IMAGE_PATH,
  // The landing carries the app node plus the site-wide Organization + WebSite nodes
  // (#241, D241.7) as a JSON-LD graph in one script.
  jsonLd: [landingJsonLd(), organizationJsonLd(), webSiteJsonLd()],
};

/** Convenience for tests/consumers wanting the resolved absolute canonical. */
export const LANDING_CANONICAL = canonicalUrl(LANDING_SEO.canonicalPath);
